import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Settings, Info } from "lucide-react";

export default function LeavePolicyConfig({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState({
    count_weekends: false,
    count_public_holidays: false,
    casual_annual: 8,
    sick_annual: 8,
    earned_annual: 15,
    maternity_annual: 180,
    cl_sl_accrual_enabled: true,
    cl_per_quarter: 2.0,
    sl_per_quarter: 2.0,
    el_per_month: 1.25,
    el_cap_junior: 30,
    el_cap_senior: 60,
    el_encashment_threshold: 15,
    el_encashment_max_per_request: 10,
  });

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const res = await api.get("/leave/policies");
      setPolicy(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      toast.error("Failed to load policy");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put("/leave/policies", policy);
      toast.success("Leave policy saved successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  const num = (field) => ({
    type: "number",
    value: policy[field],
    step: "0.5",
    min: "0",
    onChange: (e) => setPolicy({ ...policy, [field]: parseFloat(e.target.value) || 0 }),
    className: "bg-white border-gray-300 focus:border-blue-500"
  });

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6 max-w-3xl">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-md3-display-small flex items-center gap-2">
              <Settings className="h-7 w-7 text-blue-600" />
              Leave Policy Configuration
            </h1>
            <p className="text-md3-on-surface-variant mt-1">
              Define annual entitlements and accrual rules for {currentYear}
            </p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">

          {/* Annual Entitlements */}
          <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
            <CardHeader className="pb-3">
              <CardTitle className="text-md3-title-large">Annual Leave Entitlements</CardTitle>
              <p className="text-sm text-gray-500">Total days each employee is entitled to per year (used as defaults when creating allocations)</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-semibold text-blue-700">Casual Leave (CL) — days/year</Label>
                  <Input {...num("casual_annual")} data-testid="casual-annual-input" />
                  <p className="text-xs text-gray-500">
                    Accrual: {policy.cl_per_quarter} days per quarter × 4 = {policy.cl_per_quarter * 4} days/year
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold text-red-700">Sick Leave (SL) — days/year</Label>
                  <Input {...num("sick_annual")} data-testid="sick-annual-input" />
                  <p className="text-xs text-gray-500">
                    Accrual: {policy.sl_per_quarter} days per quarter × 4 = {policy.sl_per_quarter * 4} days/year
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold text-green-700">Earned Leave (EL) — days/year</Label>
                  <Input {...num("earned_annual")} data-testid="earned-annual-input" />
                  <p className="text-xs text-gray-500">
                    Accrual: {policy.el_per_month} days × 12 months = {(policy.el_per_month * 12).toFixed(1)} days/year
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold text-orange-700">Maternity Leave — days</Label>
                  <Input {...num("maternity_annual")} data-testid="maternity-annual-input" />
                  <p className="text-xs text-gray-500">Governed by Maternity Benefit Act 1961</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CL / SL Quarterly Accrual */}
          <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
            <CardHeader className="pb-3">
              <CardTitle className="text-md3-title-large">CL & SL Quarterly Accrual</CardTitle>
              <p className="text-sm text-gray-500">
                CL and SL are credited quarterly. Contract and Regular employees receive this accrual. Interns are excluded.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center gap-3 p-3 border rounded-md bg-gray-50">
                <input
                  type="checkbox"
                  id="cl_sl_accrual_enabled"
                  checked={policy.cl_sl_accrual_enabled}
                  onChange={(e) => setPolicy({ ...policy, cl_sl_accrual_enabled: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="accrual-enabled-checkbox"
                />
                <Label htmlFor="cl_sl_accrual_enabled" className="cursor-pointer font-medium">
                  Enable automatic quarterly accrual
                </Label>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="font-medium">CL credited per quarter (days)</Label>
                  <Input {...num("cl_per_quarter")} data-testid="cl-per-quarter-input" />
                  <p className="text-xs text-gray-500">
                    Credited on: Jan 1 · Apr 1 · Jul 1 · Oct 1
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium">SL credited per quarter (days)</Label>
                  <Input {...num("sl_per_quarter")} data-testid="sl-per-quarter-input" />
                  <p className="text-xs text-gray-500">
                    Mid-quarter joiners receive prorated credit based on joining date
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 flex gap-2 text-sm text-blue-800">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  CL and SL <strong>lapse at year end</strong> and do not carry forward to the next year.
                </span>
              </div>
            </CardContent>
          </Card>

          {/* EL Accrual */}
          <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
            <CardHeader className="pb-3">
              <CardTitle className="text-md3-title-large">Earned Leave (EL) Accrual</CardTitle>
              <p className="text-sm text-gray-500">EL accrues monthly and is credited at year-end. HR enters the balance manually.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label className="font-medium">EL accrual rate (days per completed month)</Label>
                <Input {...num("el_per_month")} className="max-w-xs bg-white border-gray-300" data-testid="el-per-month-input" />
                <p className="text-xs text-gray-500">
                  Standard: 1.25 days/month = 15 days/year for 12 months of service
                </p>
              </div>

              <div className="border-t pt-4">
                <Label className="font-semibold mb-3 block">EL Accumulation Caps</Label>
                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Jr. Associate → Dy. Manager (max days)</Label>
                    <Input {...num("el_cap_junior")} data-testid="el-cap-junior-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Manager and above (max days)</Label>
                    <Input {...num("el_cap_senior")} data-testid="el-cap-senior-input" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Balance exceeding the cap is eligible for encashment (HR-managed via Leave Allocations).
                </p>

                {/* Self-Service Encashment Settings */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Label className="text-sm font-semibold text-gray-800">Self-Service Encashment Application</Label>
                  <p className="text-xs text-gray-500 mb-3">Controls the form users use to apply for EL encashment (HR approves).</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">EL balance threshold (days)</Label>
                      <Input {...num("el_encashment_threshold")} data-testid="el-encashment-threshold-input" />
                      <p className="text-xs text-gray-500">Only the EL balance <strong>above</strong> this is encashable.</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Max days per request</Label>
                      <Input {...num("el_encashment_max_per_request")} data-testid="el-encashment-max-per-request-input" />
                      <p className="text-xs text-gray-500">Cap on a single encashment application.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Working Day Settings */}
          <Card className="shadow-md3-1 border-0 bg-md3-surface-container">
            <CardHeader className="pb-3">
              <CardTitle className="text-md3-title-large">Working Day Calculation</CardTitle>
              <p className="text-sm text-gray-500">Controls how leave days are counted</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="count_weekends"
                  checked={policy.count_weekends}
                  onChange={(e) => setPolicy({ ...policy, count_weekends: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="count-weekends-checkbox"
                />
                <Label htmlFor="count_weekends" className="cursor-pointer">Count weekends as leave days</Label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="count_holidays"
                  checked={policy.count_public_holidays}
                  onChange={(e) => setPolicy({ ...policy, count_public_holidays: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="count-holidays-checkbox"
                />
                <Label htmlFor="count_holidays" className="cursor-pointer">Count public holidays as leave days</Label>
              </div>
            </CardContent>
          </Card>

          {/* Policy Summary */}
          <Card className="shadow-md3-1 border border-green-200 bg-green-50">
            <CardContent className="pt-5">
              <h3 className="font-semibold text-green-800 mb-3">Policy Summary — {currentYear}</h3>
              <div className="grid grid-cols-3 gap-3 text-sm text-green-900">
                <div className="bg-white rounded p-3 border border-green-200">
                  <p className="font-semibold text-blue-700">Casual Leave</p>
                  <p className="text-2xl font-bold">{policy.casual_annual}d</p>
                  <p className="text-xs text-gray-500">{policy.cl_per_quarter}d × 4 quarters</p>
                </div>
                <div className="bg-white rounded p-3 border border-green-200">
                  <p className="font-semibold text-red-700">Sick Leave</p>
                  <p className="text-2xl font-bold">{policy.sick_annual}d</p>
                  <p className="text-xs text-gray-500">{policy.sl_per_quarter}d × 4 quarters</p>
                </div>
                <div className="bg-white rounded p-3 border border-green-200">
                  <p className="font-semibold text-green-700">Earned Leave</p>
                  <p className="text-2xl font-bold">{policy.earned_annual}d</p>
                  <p className="text-xs text-gray-500">{policy.el_per_month}d × 12 months</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={saving} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full px-8" data-testid="save-policy-btn">
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save Policy</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
